import React, { useCallback, useEffect, useState } from "react";
import { api, djApi, buildFileUrl } from "../lib/api";
import { usePubSocket } from "../lib/usePubSocket";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import StaffLoginGate from "../components/StaffLoginGate";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Loader2,
  Check,
  X,
  Megaphone,
  Plus,
  Pencil,
  RotateCcw,
  Trash2,
  LogOut,
  Wifi,
  Download as DownloadIcon,
} from "lucide-react";
import { DhunLogo } from "../components/DhunLogo";
import VibePieChart from "../components/VibePieChart";

const POLL_MS = 15000;

export default function DJDashboard() {
  const [pub, setPub] = useState(null);
  const [authed, setAuthed] = useState(!!localStorage.getItem("dd_dj_token"));
  const [shoutouts, setShoutouts] = useState([]);
  const [vibe, setVibe] = useState({ options: [], total_votes: 0, leader: null });
  const [nowPlaying, setNowPlaying] = useState(null);
  const [editOptId, setEditOptId] = useState(null);
  const [editOptName, setEditOptName] = useState("");
  const [newOptName, setNewOptName] = useState("");
  const [detail, setDetail] = useState(null);

  const loadPub = useCallback(async () => {
    if (!authed) return;
    try {
      const { data } = await djApi.get("/auth/staff/me");
      setPub(data.pub);
    } catch {
      setAuthed(false);
    }
  }, [authed]);

  useEffect(() => {
    const onUnauth = () => { setAuthed(false); setPub(null); };
    window.addEventListener("dd:dj-unauth", onUnauth);
    return () => window.removeEventListener("dd:dj-unauth", onUnauth);
  }, []);

  const loadAll = useCallback(async () => {
    if (!pub) return;
    try {
      const [s, v, np] = await Promise.all([
        djApi.get("/dj/shoutouts"),
        api.get(`/vibe?pub_id=${pub.id}`),
        djApi.get("/dj/now-playing"),
      ]);
      setShoutouts(s.data);
      setVibe(v.data);
      setNowPlaying(np.data || null);
    } catch {}
  }, [pub]);

  useEffect(() => {
    loadPub();
  }, [loadPub]);

  useEffect(() => {
    if (!pub) return;
    loadAll();
    const t = setInterval(loadAll, POLL_MS);
    return () => clearInterval(t);
  }, [pub, loadAll]);

  const action = async (id, act) => {
    try {
      await djApi.post("/shoutouts/action", { shoutout_id: id, action: act });
      const status = act === "approve" ? "approved" : act === "reject" ? "rejected" : "played";
      setShoutouts((list) => list.map((x) => (x.id === id ? { ...x, status, updated_at: new Date().toISOString() } : x)));
      setDetail(null);
      toast.success(act === "approve" ? "Approved — moved to Approved Shout-outs" : `Shout-out ${status}`);
      loadAll();
    } catch (e) {
      handleDJError(e);
    }
  };


  const resetVibe = async () => {
    try {
      await djApi.post(`/vibe/reset?pub_id=${pub.id}`);
      toast.success("Vibe round reset");
      loadAll();
    } catch (e) {
      handleDJError(e);
    }
  };

  const addOpt = async () => {
    if (!newOptName.trim()) return;
    try {
      await djApi.post("/vibe/options", { pub_id: pub.id, name: newOptName });
      toast.success("Vibe option added");
      setNewOptName("");
      loadAll();
    } catch (e) {
      handleDJError(e);
    }
  };

  const saveOpt = async (id) => {
    if (!editOptName.trim()) return;
    try {
      await djApi.patch(`/vibe/options/${id}`, { name: editOptName });
      setEditOptId(null);
      loadAll();
    } catch (e) {
      handleDJError(e);
    }
  };

  const deleteOpt = async (id) => {
    try {
      await djApi.delete(`/vibe/options/${id}`);
      toast.success("Option removed");
      loadAll();
    } catch (e) {
      handleDJError(e);
    }
  };

  const handleDJError = (e) => {
    const code = e?.response?.status;
    const detail = e?.response?.data?.detail || "Action failed";
    if (code === 401) {
      toast.error("DJ session expired. Please sign in.");
    } else {
      toast.error(detail);
    }
  };


  const logoutDJ = () => {
    localStorage.removeItem("dd_dj_token");
    setAuthed(false);
    setPub(null);
    toast.message("Signed out of DJ dashboard");
  };

  usePubSocket(pub?.id, useCallback(() => { loadAll(); }, [loadAll]));

  if (!authed) {
    return <StaffLoginGate role="dj" onLogin={() => setAuthed(true)} heading="DJ login" />;
  }

  if (!pub) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B0E14] text-white">
        <Loader2 className="h-6 w-6 animate-spin text-violet-400" />
      </div>
    );
  }

  const pending = shoutouts.filter((s) => s.status === "pending");
  const approved = shoutouts
    .filter((s) => s.status === "approved" || s.status === "played")
    .sort((a, b) => (b.updated_at || b.created_at).localeCompare(a.updated_at || a.created_at));

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white">
      <header
        className="border-b border-white/5 backdrop-blur-xl"
        style={{ background: "rgba(11,14,20,0.75)" }}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <DhunLogo size={40} />
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-violet-300">
                DJ Dashboard · {pub.name}
              </p>
              <p className="text-lg font-extrabold">Mission Control</p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-sm text-slate-300">
            {nowPlaying ? (
              <div className="rounded-full border border-emerald-400/40 bg-emerald-500/10 px-3 py-1 text-emerald-200">
                <span className="mr-1 inline-block h-2 w-2 animate-pulse rounded-full bg-emerald-400" />
                Now: <span className="font-semibold">{nowPlaying.title}</span>
              </div>
            ) : (
              <div className="rounded-full border border-white/10 px-3 py-1 text-slate-400">
                No track playing
              </div>
            )}
            <div
              className="flex items-center gap-1 rounded-full border border-violet-400/30 bg-violet-500/10 px-3 py-1 text-xs text-violet-200"
              title="Live via WebSocket"
            >
              <Wifi className="h-3 w-3" /> Live
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={logoutDJ}
              data-testid="dj-logout-button"
              className="border border-white/10 text-slate-300 hover:bg-white/5"
            >
              <LogOut className="mr-1 h-3 w-3" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 p-6 lg:grid-cols-12">
        {/* Queue */}
        {/* Shoutouts */}
        <section className="lg:col-span-8" data-testid="dj-shoutouts-section">
          <SectionHead
            title={
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-emerald-300" /> Approved Shout-outs
                {approved.length > 0 && <Badge className="border-0 bg-emerald-500/70 text-white">{approved.length}</Badge>}
              </div>
            }
            subtitle="Newest first. Tap to see the details again."
          />
          <div className="max-h-80 space-y-2 overflow-y-auto pr-1" data-testid="dj-approved-list">
            {approved.map((sh) => <ShoutoutCard key={sh.id} sh={sh} onOpen={setDetail} approved />)}
            {approved.length === 0 && (
              <p className="rounded-xl border border-dashed border-white/10 py-6 text-center text-xs text-slate-500">No approved shout-outs yet</p>
            )}
          </div>
          <div className="mt-6">
            <SectionHead
              title={
                <div className="flex items-center gap-2">
                  <Megaphone className="h-4 w-4 text-violet-300" /> Pending Shout-outs
                  {pending.length > 0 && <Badge className="border-0 bg-pink-500/80 text-white">{pending.length}</Badge>}
                </div>
              }
              subtitle="Tap a card for details. Payment is held until you approve. Reject = automatic refund."
            />
            <div className="space-y-2" data-testid="dj-pending-list">
              {pending.map((sh) => <ShoutoutCard key={sh.id} sh={sh} onOpen={setDetail} />)}
              {pending.length === 0 && (
                <p className="rounded-xl border border-dashed border-white/10 py-6 text-center text-xs text-slate-500">No pending shout-outs</p>
              )}
            </div>
          </div>
          <ShoutoutDetails sh={detail} onClose={() => setDetail(null)} onAction={action} />
        </section>

        {/* Vibe */}
        <section className="lg:col-span-4" data-testid="dj-vibe-section">
          <SectionHead title="Vibe Pulse" subtitle={`${vibe.total_votes} votes`} />
          <div className="rounded-xl border border-white/10 bg-[#131822] p-4">
            {vibe.leader && (
              <Badge
                className="mb-2 border-0 text-white"
                style={{ background: vibe.leader.color }}
              >
                Current Vibe: {vibe.leader.name}
              </Badge>
            )}
            <VibePieChart options={vibe.options} totalVotes={vibe.total_votes} compact />
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="dj-vibe-reset-button"
                  className="mt-2 w-full border border-white/10 hover:bg-white/5"
                >
                  <RotateCcw className="mr-1 h-3 w-3" /> Reset Vibe Round
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="border-white/10 bg-[#131822] text-white">
                <AlertDialogHeader>
                  <AlertDialogTitle>Reset Vibe Round?</AlertDialogTitle>
                  <AlertDialogDescription className="text-slate-400">
                    All current votes will be cleared and a fresh round starts.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="border-white/10 bg-transparent hover:bg-white/5">
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={resetVibe}
                    className="bg-pink-500 hover:bg-pink-600"
                    data-testid="dj-vibe-reset-confirm"
                  >
                    Reset
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>

          <div
            className="mt-4 space-y-2 rounded-xl border border-white/10 bg-[#131822] p-4"
            data-testid="dj-vibe-options-editor"
          >
            <p className="text-[10px] uppercase tracking-widest text-slate-400">
              Edit vibe options
            </p>
            {vibe.options.map((o) => (
              <div key={o.id} className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full" style={{ background: o.color }} />
                {editOptId === o.id ? (
                  <>
                    <Input
                      value={editOptName}
                      onChange={(e) => setEditOptName(e.target.value)}
                      className="h-8 border-white/10 bg-black/30 text-sm text-white"
                    />
                    <Button
                      size="sm"
                      onClick={() => saveOpt(o.id)}
                      className="h-8 bg-emerald-500 hover:bg-emerald-600"
                    >
                      <Check className="h-3 w-3" />
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-sm">{o.name}</span>
                    <button
                      onClick={() => {
                        setEditOptId(o.id);
                        setEditOptName(o.name);
                      }}
                      className="text-slate-400 hover:text-white"
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                    <button
                      onClick={() => deleteOpt(o.id)}
                      className="text-slate-400 hover:text-pink-400"
                      disabled={vibe.options.length <= 2}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </>
                )}
              </div>
            ))}
            {vibe.options.length < 6 && (
              <div className="flex items-center gap-2 pt-1">
                <Input
                  placeholder="New vibe..."
                  value={newOptName}
                  onChange={(e) => setNewOptName(e.target.value)}
                  data-testid="dj-vibe-new-option-input"
                  className="h-8 border-white/10 bg-black/30 text-sm text-white"
                />
                <Button
                  size="sm"
                  onClick={addOpt}
                  data-testid="dj-vibe-add-option-button"
                  className="h-8 bg-violet-500 hover:bg-violet-600"
                >
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function SectionHead({ title, subtitle }) {
  return (
    <div className="mb-3">
      <h2 className="text-lg font-extrabold tracking-tight">{title}</h2>
      {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
    </div>
  );
}

const TIER_STYLE = {
  bronze: "bg-orange-500/20 text-orange-200",
  silver: "bg-slate-300/20 text-slate-100",
  gold: "bg-amber-500/25 text-amber-200",
};

function ShoutoutCard({ sh, onOpen, approved }) {
  const tier = sh.tier || "bronze";
  return (
    <button onClick={() => onOpen(sh)} data-testid={`dj-shoutout-${sh.id}`}
      className={`w-full rounded-xl border p-3 text-left transition-colors ${approved ? "border-emerald-400/20 bg-emerald-500/5 hover:border-emerald-400/50" : "border-white/10 bg-[#131822] hover:border-violet-400/60"}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Badge className={`border-0 text-[10px] uppercase ${TIER_STYLE[tier]}`} data-testid={`dj-shoutout-tier-${sh.id}`}>{tier} · ₹{sh.price}</Badge>
        {tier !== "bronze" && sh.occasion && <Badge className="border-0 bg-pink-500/20 text-[10px] text-pink-200">{sh.occasion}</Badge>}
        <span className="ml-auto text-[10px] text-slate-500">{new Date(sh.updated_at || sh.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
      </div>
      <p className="mt-1.5 text-sm font-bold" data-testid={`dj-shoutout-name-${sh.id}`}>{sh.sender_name}</p>
      {sh.message && <p className="truncate text-xs text-slate-400">{sh.message}</p>}
    </button>
  );
}

function Row({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-[10px] uppercase tracking-widest text-slate-400">{label}</p>
      <div className="mt-0.5 text-sm text-white">{children}</div>
    </div>
  );
}

function ShoutoutDetails({ sh, onClose, onAction }) {
  if (!sh) return null;
  const tier = sh.tier || "bronze";
  const rich = tier !== "bronze";
  const photo = sh.photo_url && buildFileUrl(sh.photo_url);
  return (
    <Dialog open={!!sh} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-white/10 bg-[#131822] text-white sm:max-w-md" data-testid="dj-shoutout-details">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Badge className={`border-0 text-[10px] uppercase ${TIER_STYLE[tier]}`}>{tier} · ₹{sh.price}</Badge>
            <span className="text-sm text-slate-400">{sh.status}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Row label="Name" testid="detail-name">{sh.sender_name}</Row>
          <Row label="Message" testid="detail-message">{sh.message || <span className="text-slate-500">No message</span>}</Row>
          {rich && <Row label="Occasion" testid="detail-occasion">{sh.occasion || "—"}</Row>}
          {tier === "gold" && <Row label="Song name" testid="detail-song">{sh.song_text}</Row>}
          {rich && photo && (
            <Row label="Customer photo / uploaded file" testid="detail-photo">
              <img src={photo} alt="customer upload" className="mt-1 max-h-72 w-full rounded-lg object-contain bg-black/40" />
              <div className="mt-2 flex items-center justify-between gap-2 rounded-lg bg-black/30 px-3 py-2">
                <span className="truncate text-xs text-slate-300">{sh.photo_filename || sh.photo_path?.split("/").pop()}</span>
                <a href={`${photo}?download=1`} data-testid="detail-download"
                  className="inline-flex shrink-0 items-center gap-1 rounded-full bg-violet-500 px-3 py-1 text-xs font-bold hover:bg-violet-600">
                  <DownloadIcon className="h-3 w-3" /> Download
                </a>
              </div>
            </Row>
          )}
          {sh.status === "pending" && (
            <div className="flex gap-2 pt-2">
              <Button onClick={() => onAction(sh.id, "approve")} data-testid="detail-approve" className="flex-1 bg-emerald-500/90 hover:bg-emerald-500">
                <Check className="mr-1 h-4 w-4" /> Approve
              </Button>
              <Button variant="ghost" onClick={() => onAction(sh.id, "reject")} data-testid="detail-reject" className="border border-white/10 text-pink-300 hover:bg-pink-500/10">
                <X className="mr-1 h-4 w-4" /> Reject
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
